import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import './AIEggIndexArticle.css';

const REPO = 'https://github.com/Ceaustin117/ai-egg-index';
const LIVE = 'https://ceaustin117.github.io/ai-egg-index/';
const DATASET = 'https://huggingface.co/datasets/ceaustin117/ai-egg-index';

const AIEggIndexArticle: React.FC = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'The AI Egg Index — which free AI is actually good at everyday tasks?';
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <>
      <div className="blog-page">
        <div className="blog-container">
          <Link to="/wiki" className="back-link">← Back to Wiki</Link>

          <header className="blog-header">
            <span className="blog-eyebrow">Open Source · Benchmarking</span>
            <h1>The AI Egg Index: which <em>free</em> AI is actually good at everyday tasks?</h1>
            <p className="blog-lede">
              Nobody is properly tracking how AI actually affects the average person.
            </p>
            <p className="blog-meta">
              An open-source benchmark of free-tier LLMs · <a href={LIVE} target="_blank" rel="noopener noreferrer">live index</a> · <a href={REPO} target="_blank" rel="noopener noreferrer">code on GitHub</a>
            </p>
          </header>

          <div className="blog-body">
            <p>
              How is the AI a normal person can actually reach — the free tab, the default
              assistant — evolving over weeks, months, years, even decades? Is it getting
              better at the everyday things people lean on it for, or just better at the exams
              that make headlines? When the free model you use quietly improves — or quietly
              gets worse — who notices?
            </p>

            <p>
              Think about how strange that is. There is more money, hype, and measurement aimed
              at AI than at almost anything in modern history — and nearly all of it chases a
              single question: <em>how smart can the most expensive model in the world get?</em>{' '}
              PhD-level science exams. Competition math. Frontier models that cost real money to
              even touch. Meanwhile the AI that actually shows up in your life is the{' '}
              <strong>free</strong> one — the tab you already have open — and essentially no one
              is keeping score on whether it's any good at the things you'd actually ask it. And
              everyone just assumes it keeps getting better — a safe bet for the models you{' '}
              <em>pay</em> for, but a genuinely risky one for a free tier that a provider can
              quietly downgrade the moment it costs too much. (More on that below.)
            </p>

            <p>
              So here's something nobody's tracking: right now, a free 8-billion-parameter model
              you can open in your browser is beating Google's Gemini at the everyday questions
              regular people actually ask. The one public leaderboard that might have caught that
              shut down last year.
            </p>

            <p>
              Which leaves the question none of them answer:{' '}
              <strong>which <em>free</em> AI should you actually use?</strong>
            </p>

            <p>
              Think about how you really use this stuff. You open a chatbot to ask something real
              — this year's tax brackets, help with your kid's math homework, a quick script to
              track your spending — and a dozen free options stare back at you. Which one gets it
              right? Which one quietly makes the numbers up?
            </p>

            <p>
              So I built the <a href={LIVE} target="_blank" rel="noopener noreferrer">AI Egg Index</a>{' '}
              — an open-source benchmark that tests <strong>only free-tier models</strong>,{' '}
              <strong>only on everyday tasks</strong>, and re-runs <strong>every single week.</strong>{' '}
              It's live, it's reproducible end to end, and every number on it was committed to a
              public repo by an automated harness that anyone can audit.
            </p>

            <figure className="blog-figure">
              <a href={LIVE} target="_blank" rel="noopener noreferrer">
                <img
                  src={`${process.env.PUBLIC_URL}/ai-egg-index-leaderboard.png`}
                  alt="AI Egg Index leaderboard — a free Llama-3.1-8B on top, ahead of Google's Gemini"
                />
              </a>
              <figcaption>
                This week's board. A free Llama-3.1-8B sitting on top of Google.{' '}
                <a href={LIVE} target="_blank" rel="noopener noreferrer">See it live →</a>
              </figcaption>
            </figure>

            <h2>The last free leaderboard died last year</h2>

            <p>
              For a while there <em>was</em> a level playing field: Hugging Face's{' '}
              <strong>Open LLM Leaderboard.</strong> Submit any open-weight model, get the same
              standardized battery of evals back, no money or connections required. It was the
              closest thing the community had to a neutral public scoreboard.
            </p>

            <p>
              It was <strong>archived on March 13, 2025.</strong> The maintainers were
              refreshingly honest about why:
            </p>

            <ul className="blog-list">
              <li>
                <strong>Saturation</strong> — top models scored so high the tests stopped
                separating the good from the great. A harder "v2" bought a little time; it didn't
                fix it.
              </li>
              <li>
                <strong>Cost</strong> — re-evaluating the firehose of new releases is enormous,
                never-ending compute. Someone has to pay that bill every week.
              </li>
              <li>
                <strong>Wrong target</strong> — as the team put it, benchmarks have to chase real
                capabilities, and they'd rather kill it than let people "hill-climb irrelevant
                directions."
              </li>
            </ul>

            <p>
              Every reason is fair. But look at what <em>none</em> of them were about:{' '}
              <strong>whether the free tier of a model is any good at the stuff normal people
              actually do.</strong> The big leaderboard died chasing the frontier — bigger
              models, harder exams. That's a race with no finish line and a brutal cloud bill.
            </p>

            <p>
              The Egg Index runs the opposite play. Deliberately small: a handful of free models,
              a handful of everyday tasks, re-run cheaply every week. And unlike "how smart can
              the frontier get," this question never saturates — the answer changes every time a
              provider ships an update or quietly nerfs a free tier.
            </p>

            <h2>Why call it an "Egg Index"?</h2>

            <p>
              Economists have no shortage of ways to measure the economy — GDP, the CPI, bond
              yields — and almost none of them mean anything to a person standing in a grocery
              store. So when they need to explain inflation to actual humans, they reach for{' '}
              <strong>the price of eggs.</strong>
            </p>

            <p>
              Eggs work as a signal for a precise reason: nearly everyone buys them, buys them
              often, and remembers what they cost last month. That makes egg prices something
              people <em>feel</em> — a grounded, high-frequency read on the cost of living that
              cuts straight through the abstract aggregates. You don't need an economics degree
              to know that when eggs double, the grocery bill hurts. One legible staple carries
              more real information for a regular person than the "correct" macro number ever
              will.
            </p>

            <p>
              AI benchmarks have the exact opposite problem. They're all GDP and no eggs —
              abstract aggregate scores (MMLU, GPQA, competition math) that track the ceiling of
              what the smartest <em>paid</em> models can do, and mean almost nothing to someone
              deciding which free chatbot to open. There's no staple-good measure for AI. No
              price of eggs.
            </p>

            <p>
              So I made one. The AI Egg Index is deliberately the <em>egg</em>: a small basket of
              everyday tasks, priced on the free models regular people can actually reach,
              re-checked often enough to catch the number moving. Not the smartest AI in the
              world — the one thing you'd actually notice: whether the free AI in front of you is
              any good at real work. <strong>The everyday index for AI.</strong>
            </p>

            <h2>It's not a snapshot. It's a time machine.</h2>

            <p>Here's the part that compounds — and the part I'm most excited about.</p>

            <p>
              A single egg price is trivia. <em>Decades</em> of egg prices are the story of
              inflation itself. Same logic here. Because the index re-runs the same tasks every
              week and stamps every score with a methodology version and a git commit, it isn't a
              one-off screenshot — it's the start of a <strong>public time series on how free AI
              actually develops.</strong> Not launch-day press releases; the real thing regular
              people can reach, measured the same way, week after week.
            </p>

            <div className="blog-callout callout-warn">
              <p>
                And there's a sharper reason to watch it than curiosity. It's tempting to assume
                all of this only ever gets better — but that assumption is only safe for the{' '}
                <em>paid</em> models. Frontier labs are in an arms race to make what you pay for
                smarter, and that line reliably points up. Free tiers are the opposite. A free
                tier isn't a product; it's a cost center — and every incentive points toward
                quietly degrading it. A smaller quantized model swapped in behind the same name.
                Tighter limits. A cheaper backend. All to nudge you onto the paid plan. Call it{' '}
                <strong>enshittification, AI edition:</strong> "paid gets better" is a decent bet,
                but "free gets better" is not — and no provider is going to publish a press
                release the week they make the free tier worse. The only way anyone finds out is
                by measuring it, in the open, over time.
              </p>
            </div>

            <p>
              Six months from now the interesting artifact won't be today's leaderboard. It'll be
              the <strong>curve</strong>: the week free AI caught up to last year's paid
              flagships, the provider that quietly regressed and hoped nobody would run the
              numbers, the moment "good enough for everyday work" became free for everyone.
              Nobody is keeping that record. So this does.
            </p>

            <h2>What's actually on the test</h2>

            <p>Four benchmarks, every one chosen around what people actually ask AI to do:</p>

            <ul className="blog-list">
              <li>
                <strong>Practical knowledge</strong> — tax brackets, retirement limits, minimum
                wage, consumer rights. <em>Does it actually know real-world facts — and are they
                current?</em>
              </li>
              <li>
                <strong>Instruction following (IFEval)</strong> — "format as a table," "answer in
                exactly three bullets." <em>Does it do what you actually asked?</em>
              </li>
              <li>
                <strong>Grade-school math (GSM8K)</strong> — word problems. <em>If it can't help
                with homework, how useful is it?</em>
              </li>
              <li>
                <strong>Creative + technical</strong> — "write a budget tracker that outputs its
                summaries as haiku." <em>Can it blend code and creativity the way real requests
                do?</em>
              </li>
            </ul>

            <p>
              The two custom benchmarks (Practical + Creative) are the ones every provider runs,
              so the <strong>Overall</strong> ranking is built from those — and a model has to
              complete <em>both</em> to be ranked at all. No crowning a model that only showed up
              for the easy half.
            </p>

            <h2>The result that made me keep going</h2>

            <p>
              As of launch, the model on top isn't a household name. It's a{' '}
              <strong>free 8-billion-parameter model</strong> — Llama-3.1-8B on Groq — sitting{' '}
              <em>ahead of bigger, more famous free models</em> on the everyday tasks. Turns out
              you don't need the giant model to get real work done. You need the right one, and
              until now nobody was telling you which that was.
            </p>

            <p>
              I'll be straight with you: <strong>these are small-sample, directional scores, not
              three-decimal gospel.</strong> That's a deliberate choice — I'd rather ship an
              honest rough signal than fake precision. The whole point is that it re-runs every
              week, so the signal sharpens over time instead of rotting.{' '}
              <a href={LIVE} target="_blank" rel="noopener noreferrer">Check the live board</a>{' '}
              for where things stand today.
            </p>

            <h2>Try it in 30 seconds</h2>

            <p>
              Everything is open — the harness, the prompts, every single result. Clone it and
              run the exact same eval I do:
            </p>

            <div className="blog-code">
              <pre>
                <span className="code-comment"># Clone and install</span>{'\n'}
                git clone https://github.com/Ceaustin117/ai-egg-index{'\n'}
                cd ai-egg-index &amp;&amp; pip install -r requirements.txt{'\n'}
                {'\n'}
                <span className="code-comment"># Set your free Groq key</span>{'\n'}
                export GROQ_API_KEY=your_free_key{'\n'}
                {'\n'}
                <span className="code-comment"># Run the same eval I do</span>{'\n'}
                python benchmarks/practical-knowledge/eval.py --provider groq --model llama-3.1-8b-instant --limit 3{'\n'}
                python aggregate.py
              </pre>
            </div>

            <p>
              The custom benchmarks are graded by an LLM-as-judge — deliberately a{' '}
              <em>stronger</em> free model (Llama-3.3-70B) than anything under test, because the
              grader is infrastructure, not a contestant. IFEval and GSM8K run alongside as
              familiar reference points.
            </p>

            <h2>The rules I won't break</h2>

            <p>
              The fastest way to make a benchmark worthless is to quietly put a thumb on the
              scale. So the guarantees are written down, in public, and enforced by the code:
            </p>

            <ul className="blog-list">
              <li>
                <strong>Neutral by construction</strong> — every model gets identical prompts and
                scoring from a public automated harness that commits its own results. No
                pay-to-list, no sponsored slots, no private pre-testing. Ever.
              </li>
              <li>
                <strong>Honest about limits</strong> — the caveats are documented, not buried: a
                cross-model "overall" that only ranks on the benchmarks every provider runs, and
                free tiers that can silently change under a stable name. It's all in{' '}
                <a href={`${REPO}/blob/main/LIMITATIONS.md`} target="_blank" rel="noopener noreferrer">LIMITATIONS.md</a>.
              </li>
              <li>
                <strong>Contamination-aware</strong> — prompts carry a canary string, with a
                written{' '}
                <a href={`${REPO}/blob/main/CONTAMINATION.md`} target="_blank" rel="noopener noreferrer">contamination policy</a>.
              </li>
              <li>
                <strong>Versioned forever</strong> — every score is stamped with a methodology
                version and commit, so the historical curve stays honest and comparable.
              </li>
            </ul>

            <h2>Come build it with me</h2>

            <p>
              This is the fun part. A benchmark of "what regular people ask AI" is only as good as
              its questions — and you know questions I haven't thought of.
            </p>

            <ul className="blog-list">
              <li><strong>Add a task</strong> — a real thing you'd ask a free AI, with a scoring rubric.</li>
              <li><strong>Add a provider</strong> — another free tier worth tracking.</li>
              <li><strong>Sharpen the scoring</strong> — better judges, better rubrics, better honesty.</li>
            </ul>

            <p>
              There are{' '}
              <a href={`${REPO}/contribute`} target="_blank" rel="noopener noreferrer">good first issues</a>{' '}
              waiting, contributions are credited automatically, and if you just want to follow
              the story, <strong>starring the repo is the single most helpful thing you can
              do</strong> — it's how these projects find the people who care.
            </p>

            <ul className="blog-cta">
              <li><a href={REPO} target="_blank" rel="noopener noreferrer">⭐ <strong>Star / fork the code</strong> — github.com/Ceaustin117/ai-egg-index</a></li>
              <li><a href={LIVE} target="_blank" rel="noopener noreferrer">🥚 <strong>Watch the live index</strong> — ceaustin117.github.io/ai-egg-index</a></li>
              <li><a href={DATASET} target="_blank" rel="noopener noreferrer">📊 <strong>Grab the raw dataset</strong> — huggingface.co/datasets/ceaustin117/ai-egg-index</a></li>
            </ul>

            <p>
              If you've ever wondered which free AI to actually trust with something that matters,
              that's the whole reason this exists. A fresh winner drops every week. Come see who's
              on top.
            </p>

            <p className="blog-cite">
              Cite: DOI{' '}
              <a href="https://doi.org/10.5281/zenodo.21343272" target="_blank" rel="noopener noreferrer">10.5281/zenodo.21343272</a>.
              {' '}Code MIT, data CC BY 4.0.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AIEggIndexArticle;
